import React from 'react'
import { Drawer, Descriptions, Divider, Space, Button, Tag } from 'antd'
import { CreditCardOutlined, CheckCircleOutlined, UndoOutlined } from '@ant-design/icons'
import type { Payment } from '../types/payment.types'
import { formatINR, formatIST } from '../../../shared/utils/formatters'

interface PaymentDetailsDrawerProps {
    open: boolean
    onClose: () => void
    payment: Payment | null
    onRefundClick: (payment: Payment) => void
}

export const PaymentDetailsDrawer: React.FC<PaymentDetailsDrawerProps> = ({
    open,
    onClose,
    payment,
    onRefundClick
}) => {
    if (!payment) return null

    return (
        <Drawer
            title={`Payment Details - ${payment.id}`}
            placement="right"
            width={500}
            onClose={onClose}
            open={open}
            extra={
                <Space>
                    {payment.status === 'success' && (
                        <Button type="primary" danger icon={<UndoOutlined />} onClick={() => onRefundClick(payment)}>
                            Issue Refund
                        </Button>
                    )}
                </Space>
            }
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Descriptions title="Payment Information" column={1} bordered>
                    <Descriptions.Item label="Payment ID">
                        <strong>{payment.id}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Order ID">{payment.orderId}</Descriptions.Item>
                    <Descriptions.Item label="Transaction ID">
                        {payment.transactionId}
                    </Descriptions.Item>
                    <Descriptions.Item label="Customer">{payment.customer}</Descriptions.Item>
                    <Descriptions.Item label="Merchant">{payment.merchant}</Descriptions.Item>
                    <Descriptions.Item label="Amount">
                        <strong style={{ fontSize: 18, color: '#1890ff' }}>
                            {formatINR(payment.amount)}
                        </strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Method">
                        <Tag icon={<CreditCardOutlined />}>{payment.method.toUpperCase()}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag
                            icon={payment.status === 'success' ? <CheckCircleOutlined /> : undefined}
                            color={payment.status === 'success' ? 'success' : payment.status === 'failed' ? 'error' : 'warning'}
                        >
                            {payment.status.toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Date">{formatIST(payment.createdAt)}</Descriptions.Item>
                </Descriptions>

                <Descriptions title="Payment Method Details" column={1} bordered>
                    {payment.method === 'card' && payment.cardLast4 && (
                        <Descriptions.Item label="Card Number">
                            **** **** **** {payment.cardLast4}
                        </Descriptions.Item>
                    )}
                    {payment.method === 'upi' && payment.upiId && (
                        <Descriptions.Item label="UPI ID">{payment.upiId}</Descriptions.Item>
                    )}
                    {payment.method === 'netbanking' && (
                        <Descriptions.Item label="Bank">HDFC Bank</Descriptions.Item>
                    )}
                    {payment.method === 'wallet' && (
                        <Descriptions.Item label="Wallet">Paytm Wallet</Descriptions.Item>
                    )}
                </Descriptions>

                <Descriptions title="Gateway Response" column={1} bordered>
                    <Descriptions.Item label="Response">
                        {payment.gatewayResponse || 'N/A'}
                    </Descriptions.Item>
                </Descriptions>

                <Divider />

                <Space>
                    {payment.status === 'success' && (
                        <Button
                            danger
                            icon={<UndoOutlined />}
                            onClick={() => onRefundClick(payment)}
                        >
                            Process Refund
                        </Button>
                    )}
                </Space>
            </Space>
        </Drawer>
    )
}
